import { FeatureTypes, LinkTypes } from "entity-app/constants";
import { getImplicitMaterialDisplayTitle } from "entity-app/shared-components/entity-dateblock/utils/implicit-material-title";
import { LeadTimeLine } from "pages/design-package-details-page/components/view-dependencies/model";
import { bidPackageLabel } from "pages/planning-workspace/bid-package-label";

export interface RawOnSiteEntity {
  id?: string;
  number?: string | null;
  title?: string | null;
  auto_inc_value?: string | number | null;
  feature_type_id?: number | null;
  implicit?: boolean | null;
  parentSubmittals?: Array<{
    source_feature_instance?: {
      auto_inc_value?: string | number | null;
    } | null;
  }> | null;
  feature_workflow_instance?: {
    lead_time?: number | null;
    final_deadline_date?: string | null;
    manual_entry_date?: string | null;
  } | null;
  workflow_instance_milestones?: Array<{
    sequence_no?: number | null;
    hidden?: boolean | null;
    feature_workflow_instance_offsets_as_src?: Array<{
      name?: string | null;
      duration?: number | null;
    }> | null;
  }> | null;
  feature_activities?: Array<{
    start_date?: string | null;
    end_date?: string | null;
  }> | null;
  governingLinks?: RawOnSiteLink[] | null;
}

export interface RawOnSiteLink {
  id?: string;
  link_type?: number | null;
  target_feature_instance?: RawOnSiteEntity | null;
}

export interface RawOnSiteBidPackage {
  id?: string;
  number?: string | null;
  title?: string | null;
  feature_workflow_instance?: { manual_entry_date?: string | null } | null;
  governingLinks?: RawOnSiteLink[] | null;
}

export interface RequiredOnSite {
  /** Bid package's own manual deadline: no lead time and no hover. */
  ownDeadline: boolean;
  /** Id of the entity named by `title`, marked in "View All Links". */
  governingId: string | null;
  title: string;
  dateLabel: "Start Date" | "End Date" | "Deadline" | "Date";
  date: string;
  leadLines: LeadTimeLine[];
  totalDays: number;
}

const SUBMITTAL_HEADING =
  "Information of Submittal Plan or Submittal that is used for back computation to the Bid Package";
const GOVERNING_SUBMITTAL_HEADING =
  "Information of governing Submittal that is used for back computation to the Bid Package";

function typeOf(entity: RawOnSiteEntity): number {
  return Number(entity.feature_type_id);
}

function isType(entity: RawOnSiteEntity, type: FeatureTypes): boolean {
  return typeOf(entity) === (type as number);
}

function deadlineOf(entity: RawOnSiteEntity): string | null {
  const wf = entity.feature_workflow_instance;
  return wf?.final_deadline_date || wf?.manual_entry_date || null;
}

function heading(label: string): LeadTimeLine {
  return { label, days: 0, section: true };
}

/** Prep is the first milestone's offset; review is the rest of the offsets. */
function prepAndReview(entity: RawOnSiteEntity): {
  prep: number;
  review: number;
} {
  const days = (entity.workflow_instance_milestones ?? [])
    .filter((milestone) => !milestone.hidden)
    .map((milestone) =>
      (milestone.feature_workflow_instance_offsets_as_src ?? []).reduce(
        (sum, offset) => sum + (offset.duration ?? 0),
        0
      )
    );
  const [prep = 0, ...rest] = days;
  return { prep, review: rest.reduce((sum, day) => sum + day, 0) };
}

/** Lead Time field when set, else the plan's own offsets. */
function leadTimeOf(entity: RawOnSiteEntity): number {
  const leadTime = entity.feature_workflow_instance?.lead_time;
  if (leadTime != null) return Number(leadTime);
  return (entity.workflow_instance_milestones ?? []).reduce(
    (sum, milestone) =>
      sum +
      (milestone.feature_workflow_instance_offsets_as_src ?? []).reduce(
        (acc, offset) => acc + (offset.duration ?? 0),
        0
      ),
    0
  );
}

function submittalLines(
  submittal: RawOnSiteEntity,
  plan: boolean
): LeadTimeLine[] {
  const { prep, review } = prepAndReview(submittal);
  const prefix = plan ? "Submittal Plan's" : "Submittal";
  return [
    { label: `${prefix} Prep Time`, days: prep },
    { label: `${prefix} Review Time`, days: review }
  ].filter((line) => line.days > 0);
}

function materialLines(material: RawOnSiteEntity, plan: boolean) {
  const days = leadTimeOf(material);
  if (days <= 0) return [];
  return [
    {
      label: plan ? "Material Plan Lead Time" : "Material Lead Time",
      days
    }
  ];
}

interface Step {
  link: RawOnSiteLink;
  entity: RawOnSiteEntity;
}

/** Every governing path from the bid package, three hops at most. */
function governingPaths(bp: RawOnSiteBidPackage): Step[][] {
  const paths: Step[][] = [];
  const walk = (links: RawOnSiteLink[] | null | undefined, path: Step[]) => {
    const steps = (links ?? []).flatMap((link) =>
      link.target_feature_instance
        ? [{ link, entity: link.target_feature_instance }]
        : []
    );
    if (steps.length === 0) {
      if (path.length) paths.push(path);
      return;
    }
    steps.forEach((step) => {
      const next = [...path, step];
      // Activities and Onsite Estimates end the chain.
      if (
        isType(step.entity, FeatureTypes.ACTIVITIES) ||
        isType(step.entity, FeatureTypes.PLANNING_ONLY_ACTIVITY)
      ) {
        paths.push(next);
        return;
      }
      walk(step.entity.governingLinks, next);
    });
  };
  walk(bp.governingLinks, []);
  return paths;
}

function activityDate(step: Step): {
  dateLabel: "Start Date" | "End Date";
  date: string;
} | null {
  const linkType = step.link.link_type ?? LinkTypes.finish_to_start;
  const useEnd =
    linkType === (LinkTypes.finish_to_finish as number) ||
    linkType === (LinkTypes.start_to_finish as number);
  const dates = (step.entity.feature_activities ?? [])
    .map((activity) => (useEnd ? activity.end_date : activity.start_date))
    .filter((date): date is string => Boolean(date))
    .sort((a, b) => Date.parse(a) - Date.parse(b));
  return dates[0]
    ? { dateLabel: useEnd ? "End Date" : "Start Date", date: dates[0] }
    : null;
}

/** Rows 1 & 2: BP -> Submittal Plan -> Material Plan -> Activity / estimate. */
function planLines(path: Step[]): LeadTimeLine[] {
  const plan = path.find((step) =>
    isType(step.entity, FeatureTypes.PLANNING_ONLY_SUBMITTAL)
  );
  const materialPlan = path.find((step) =>
    isType(step.entity, FeatureTypes.PLANNING_ONLY_MATERIAL)
  );
  return [
    ...(plan ? submittalLines(plan.entity, true) : []),
    ...(materialPlan ? materialLines(materialPlan.entity, true) : [])
  ];
}

/** Row 3: the bid package's governing Material (directly or via a plan). */
function materialHoverLines(
  submittal: Step | undefined,
  material: Step
): LeadTimeLine[] {
  const subLines = submittal ? submittalLines(submittal.entity, false) : [];
  const matLines = materialLines(material.entity, false);
  return [
    ...(subLines.length ? [heading(SUBMITTAL_HEADING), ...subLines] : []),
    ...(matLines.length
      ? [heading("Information of Material governing Bid Package"), ...matLines]
      : [])
  ];
}

/** Row 4: the bid package's governing Submittal. */
function submittalHoverLines(
  submittal: Step,
  material: Step | undefined
): LeadTimeLine[] {
  const matLines = material ? materialLines(material.entity, false) : [];
  return [
    heading(GOVERNING_SUBMITTAL_HEADING),
    ...submittalLines(submittal.entity, false),
    ...(matLines.length
      ? [
          heading("Information of Material governing the Submittal"),
          ...matLines
        ]
      : [])
  ];
}

/** Implicit materials are named after their submittal, not their UUID title. */
function ownerTitle(
  owner: RawOnSiteEntity,
  submittal: RawOnSiteEntity | undefined
): string {
  if (!owner.implicit) return owner.title ?? "";
  const sequenceId =
    submittal?.auto_inc_value ??
    owner.parentSubmittals?.[0]?.source_feature_instance?.auto_inc_value;
  return getImplicitMaterialDisplayTitle(sequenceId);
}

type Resolved = Omit<RequiredOnSite, "totalDays" | "ownDeadline"> & {
  rank: number;
};

/** One governing path resolved by the requirement table. */
function resolvePath(path: Step[]): Resolved | null {
  const last = path[path.length - 1];
  const material = path.find((step) =>
    isType(step.entity, FeatureTypes.MATERIALS)
  );
  const submittal = path.find((step) =>
    isType(step.entity, FeatureTypes.SUBMITTALS)
  );
  const submittalOrPlan = path.find(
    (step) =>
      isType(step.entity, FeatureTypes.SUBMITTALS) ||
      isType(step.entity, FeatureTypes.PLANNING_ONLY_SUBMITTAL)
  );
  // A real Submittal governs the bid package directly (row 4); a Material
  // reached directly or through a Submittal Plan is row 3; else rows 1 & 2.
  const viaSubmittal = isType(path[0].entity, FeatureTypes.SUBMITTALS);
  const leadLines = viaSubmittal
    ? submittalHoverLines(path[0], material)
    : material
      ? materialHoverLines(submittalOrPlan, material)
      : planLines(path);

  if (isType(last.entity, FeatureTypes.ACTIVITIES)) {
    const date = activityDate(last);
    if (!date) return null;
    return {
      governingId: last.entity.id ?? null,
      title: last.entity.title ?? "",
      ...date,
      leadLines,
      rank: 0
    };
  }
  if (isType(last.entity, FeatureTypes.PLANNING_ONLY_ACTIVITY)) {
    const date = last.entity.feature_workflow_instance?.manual_entry_date;
    if (!date) return null;
    return {
      governingId: last.entity.id ?? null,
      title: "Onsite Estimate",
      dateLabel: "Date",
      date,
      leadLines,
      rank: 0
    };
  }
  // Chain stops at a Material or Submittal governed by its own date. A
  // successor Material is ignored: the first Material in the chain is named.
  const owner = material ?? submittal;
  if (!owner) return null;
  const date = deadlineOf(owner.entity);
  if (!date) return null;
  return {
    governingId: owner.entity.id ?? null,
    title: ownerTitle(owner.entity, submittal?.entity),
    dateLabel: "Deadline",
    date,
    leadLines,
    rank: 1
  };
}

/**
 * Required on Site for one bid package, per ai-seed/requirement.csv. Submittal
 * Plans and Material Plans are walked through but never named. When several
 * governing paths resolve, an activity or onsite estimate beats an entity's
 * own deadline, then the earliest date wins.
 */
export function resolveRequiredOnSite(
  bp: RawOnSiteBidPackage | null | undefined
): RequiredOnSite | null {
  if (!bp) return null;
  const chosen = governingPaths(bp)
    .map(resolvePath)
    .filter((item): item is Resolved => item !== null)
    .sort(
      (a, b) => a.rank - b.rank || Date.parse(a.date) - Date.parse(b.date)
    )[0];
  if (chosen) {
    return {
      ownDeadline: false,
      governingId: chosen.governingId,
      title: chosen.title,
      dateLabel: chosen.dateLabel,
      date: chosen.date,
      leadLines: chosen.leadLines,
      totalDays: chosen.leadLines.reduce(
        (sum, line) => sum + (line.section ? 0 : line.days),
        0
      )
    };
  }
  // Row 5: no governing chain; the bid package's own manual deadline.
  const ownDate = bp.feature_workflow_instance?.manual_entry_date;
  if (!ownDate) return null;
  return {
    ownDeadline: true,
    governingId: bp.id ?? null,
    title: bidPackageLabel(bp.number, bp.title),
    dateLabel: "Deadline",
    date: ownDate,
    leadLines: [],
    totalDays: 0
  };
}
